import { memo, useMemo } from 'react';
import { cn } from '@divlab/divanui';

import useTranslation from '@Queries/useTranslation';
import useRouter from '@Navigation/useRouter';
import Link from '@Navigation/Link';
import formatDate from '@Utils/formatDate';
import formatReviewAuthorName from '@Utils/formatReviewAuthorName';
import ReviewGallery from './elems/ReviewGallery';
import styles from './Review.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { ReviewData } from '@Types/Review';

export interface ReviewProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  review: ReviewData;
  onClickImage?: (e: MouseEvent, photoId: number) => void;
}

const Review: FC<ReviewProps> = (props) => {
  const { className, review, onClickImage, ...restProps } = props;
  const { t } = useTranslation();
  const router = useRouter();
  const [day, month, year] = review.created_at.split('.').map(Number);

  const name = useMemo(() => formatReviewAuthorName(review.author), [review.author]);

  return (
    <div
      {...restProps}
      className={cn(styles.review, className)}
      itemProp='review'
      itemScope
      itemType='http://schema.org/Review'
    >
      <div className={styles.head} itemProp='author' itemScope itemType='http://schema.org/Person'>
        <div className={styles.avatar}>{name.initials}</div>
        <div className={styles.info}>
          <div>
            <div className={styles.name} itemProp='name'>
              {name.shortName}
            </div>
            <div className={styles.date}>{formatDate(new Date(year, month - 1, day))}</div>
          </div>
          {review.isMemberLoyaltyProgram && (
            <Link
              to={router.club}
              target='_blank'
              className={styles.memberLoyaltyProgram}
              view='primary'
            >
              {t('ui.club-member')}
            </Link>
          )}
        </div>
      </div>

      <div className={styles.wrapperMain}>
        <div className={styles.content}>
          <div className={styles.text} itemProp='reviewBody'>
            {review.text}
          </div>

          {review.photos.length > 0 && (
            <ReviewGallery
              photos={review.photos}
              count={3}
              onClickImage={onClickImage}
              className={styles.reviewGallery}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(Review);
